import _ from 'lodash';
import { change, formValueSelector, arrayRemove } from 'redux-form';
import { replace } from 'react-router-redux';

import { POS_MAX_ITEM_QUANTITY } from 'Common/Constants';
import * as DialogActions from 'Components/dialogs/state/actions';
import { TransactionEnum } from 'Common/FieldEnum';
import { TRANSACTION_FORM_NAME } from 'Common/FormEnum';
import {
  getProhibitedMessage,
  getItemWithData,
} from 'PointOfSale/modules/common/helpers';
import { StatusEnum } from 'Common/Constants/Enumeration/StatusEnum';
import { POS_ROUTES } from 'PointOfSale/modules/common/constants';
import * as POSSelectors from 'PointOfSale/selectors';
import * as POSTypes from 'PointOfSale/state/types';
import * as CommonSelectors from 'State/selectors';

import * as types from './types';

const transactionFormSelector = formValueSelector(TRANSACTION_FORM_NAME);

/**
 * Resets the state of the current selected items
 * Removes selected items
 */
export const reset = () => (dispatch, getState) => {
  const boughtItems = [];
  const values = transactionFormSelector(getState(), TransactionEnum.Values);

  dispatch(
    change(TRANSACTION_FORM_NAME, TransactionEnum.BoughtItems, boughtItems)
  );
  if (values && values[TransactionEnum.BoughtItems]) {
    values[TransactionEnum.BoughtItems] = boughtItems;
    dispatch(change(TRANSACTION_FORM_NAME, TransactionEnum.Values, values));
  }
};

/**
 * Adds the quick serve item to the current items if set
 */
export const addQuickServeItem = () => (dispatch, getState) => {
  const state = getState();

  const profile = POSSelectors.getProfile(state);
  const qsi = POSSelectors.getQuickServeItem(state);
  const autoServe = POSSelectors.getAutoServe(state);

  const studentIdSeed = profile.IdSeed;

  if (studentIdSeed && profile.StatusCurrent !== StatusEnum.Withdrawn) {
    // if there is a quick serve item, add to the selected profile
    if (!_.isEmpty(qsi)) {
      const boughtItems =
        transactionFormSelector(state, TransactionEnum.BoughtItems) || [];

      const index = boughtItems.findIndex((bi) => bi.ItemId === qsi.ItemId);

      //item shouldn't already exist in boughtItems
      if (index > -1) {
        return;
      }

      //add new item
      const pendingItem = getItemWithData(qsi, profile);
      const pendingItems = [pendingItem];

      //make sure the item can be added
      const prohibitedItems = POSSelectors.getProhibitedItems(state) || [];
      const currentSelectedMenu = POSSelectors.getCurrentSelectedMenu(state);
      const taxRate = POSSelectors.getCurrentTaxRate(state);
      const isOnline = CommonSelectors.getOnline(state);

      const notificationObj = getProhibitedMessage({
        profile,
        item: qsi,
        currentSelectedMenu,
        prohibitedItems,
        boughtItems: [],
        pendingItems,
        taxRate,
        isOnline,
      });

      if (notificationObj) {
        if (autoServe) {
          dispatch(replace(`${POS_ROUTES.QUEUE}/${profile.Id}`));
        }

        dispatch(DialogActions.setNotificationDialog(notificationObj));

        return;
      }

      dispatch(
        change(TRANSACTION_FORM_NAME, TransactionEnum.BoughtItems, pendingItems)
      );
    }
  }
};

/**
 * Adds an item to the bought items list if it does not exist, otherwise it increments
 * its current count. It checks to see if an item is prohibited before adding it.
 *
 * @param {Object} item
 * @param {Number} quantity
 * @param {Boolean} setQuantity - determines whether the quantity should be added onto the current quantity or be used as the updated quantity
 */
export const addItem =
  (item, quantity = 1, setQuantity = false) =>
  (dispatch, getState) => {
    if (quantity < 1) {
      return;
    }

    const state = getState();
    const profile = POSSelectors.getProfile(state);
    const boughtItems = POSSelectors.getCurrentTransactionBoughtItems(state);

    //first, figure out what actions we're going to take
    //and apply those actions to a new object (so we can test before dispatching)
    const pendingItems = _.cloneDeep(boughtItems);

    const index = boughtItems.findIndex((bi) => bi.ItemId === item.ItemId);

    if (index > -1) {
      // Item exists, increase its count
      const currentQty = boughtItems[index].Qty;

      let updatedQty = setQuantity ? quantity : currentQty + quantity;

      if (updatedQty > POS_MAX_ITEM_QUANTITY) {
        updatedQty = POS_MAX_ITEM_QUANTITY;
      }

      //update our pending item
      pendingItems[index].Qty = updatedQty;
      pendingItems[index].Price = _.multiply(
        pendingItems[index].UnitPrice,
        updatedQty
      );
    } else {
      // New item, add it to our pending items
      const pendingItem = getItemWithData(item, profile, quantity);
      pendingItems.push(pendingItem);
    }

    //make sure the new item can actually be added
    const prohibitedItems = POSSelectors.getProhibitedItems(state);
    const currentSelectedMenu = POSSelectors.getCurrentSelectedMenu(state);
    const taxRate = POSSelectors.getCurrentTaxRate(state);
    const isOnline = CommonSelectors.getOnline(state);

    const notification = getProhibitedMessage({
      profile,
      item,
      currentSelectedMenu,
      prohibitedItems,
      boughtItems,
      pendingItems,
      taxRate,
      isOnline,
    });

    if (notification) {
      dispatch(DialogActions.setNotificationDialog(notification));
      return;
    }

    dispatch({
      type: types.ADD_ITEM_TO_TRANSACTION,
      payload: {
        item: {
          ItemId: item.ItemId,
          Qty: quantity,
          NutritionCode: item.NutritionCode,
        },
        setQuantity,
      },
    });
  };

/**
 * Removes an item from the current transaction.
 * @param {String} itemId
 * @param {Boolean} all - true to remove all, otherwise decrement the count
 */
export const removeItem =
  (itemId, all = false) =>
  (dispatch, getState) => {
    const state = getState();

    const boughtItems =
      transactionFormSelector(state, TransactionEnum.BoughtItems) || [];

    const values = transactionFormSelector(state, 'values');

    const index = boughtItems.findIndex((bi) => bi.ItemId === itemId);

    if (index > -1) {
      const currentQty = boughtItems[index].Qty;

      if (all) {
        dispatch(
          arrayRemove(TRANSACTION_FORM_NAME, TransactionEnum.BoughtItems, index)
        );
        values.BoughtItems.splice(index, 1);
        dispatch(change(TRANSACTION_FORM_NAME, 'values', values));

        return;
      }

      // Remove item from current transaction if 0
      if (currentQty - 1 === 0) {
        dispatch(
          arrayRemove(TRANSACTION_FORM_NAME, TransactionEnum.BoughtItems, index)
        );
      } else {
        // Decrement the current count
        dispatch(
          change(
            TRANSACTION_FORM_NAME,
            `${TransactionEnum.BoughtItems}[${index}].Qty`,
            currentQty - 1
          )
        );
        dispatch(
          change(
            TRANSACTION_FORM_NAME,
            `values.${TransactionEnum.BoughtItems}[${index}].Qty`,
            currentQty - 1
          )
        );
      }
    }
  };

export const setSymbolIsOpen = (isOpen, index) => ({
  type: types.SERVING_SET_IS_OPEN_SYMBOL,
  payload: { isOpen, index },
});

/**
 *
 * @param {Array.<{isOpen: Boolean, index: Number}>} payload
 */
export const setSymbolsIsOpen = (payload = []) => ({
  type: types.SERVING_SET_SYMBOLS_IS_OPEN,
  payload,
});

export const setIsInvalidStudentSelected = (value) => ({
  type: types.SERVING_SET_IS_INVALID_STUDENT_SELECTED,
  payload: value,
});

export const setCurrentProfile = (profile) => (dispatch, getState) => {
  const currentProfile = {};
  currentProfile.profile = profile;
  dispatch({
    type: types.SET_CURRENT_PROFILE,
    payload: currentProfile,
  });
};

export const clearCurrentProfile = () => ({
  type: types.CLEAR_CURRENT_PROFILE,
});

export const clearCurrentTransaction = () => ({
  type: POSTypes.RESET_CURRENT_TRANSACTION,
});

export const updateBalanceInProfile = (balance) => {
  return { type: types.UPDATE_PROFILE_BALANCE, payload: balance };
};

export const updatePreviousAmountPaid = (input) => {
  return {
    type: types.UPDATE_PROFILE_PREVIOUS_AMOUNT_PAID,
    payload: input,
  };
};

export const setProfile = (profile) => ({
  type: types.SET_PROFILE,
  payload: profile,
});

/**
 * Updates the last transaction profile to the current profile
 */
export const setLastTransactionProfile = () => (dispatch, getState) => {
  const state = getState();
  const isOnline = CommonSelectors.getOnline(state);
  const profile = isOnline ? POSSelectors.getProfile(state) : {};

  dispatch({
    type: types.SET_LAST_TRANSACTION_PROFILE,
    payload: profile,
  });
};

export const changeUnitPrice = (unitPrice, itemId) => (dispatch, getState) => {
  const state = getState();

  const boughtItems =
    transactionFormSelector(state, TransactionEnum.BoughtItems) || [];
  const index = boughtItems.findIndex((bi) => bi.ItemId === itemId);

  if (index > -1) {
    dispatch(
      change(
        TRANSACTION_FORM_NAME,
        `${TransactionEnum.BoughtItems}[${index}].${TransactionEnum.UnitPrice}`,
        unitPrice
      )
    );
  }
};



// WEBPACK FOOTER //
// ./src/pages/pointofsale/modules/individualServing/state/actions.js